import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useContext } from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext from 'src/contexts/PlansContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { AllocateHistoryEntity } from 'src/entities/AllocateHistory.entity';
import { OrderAssignment } from 'src/entities/orderEntity';
import { BaseOperationEntity, distanceMmBurdenPerShippersEntity, PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsOperationEntityWithStatsEntity } from 'src/entities/PlanningsOperationEntityWithStats.entity';
import arrayUtil from 'src/utils/array.util';

import { useMutationPlanning } from './useQueryPlanning';

interface RollbackRequestHookParams {
  currentHistoryVersion: string;
  resetCurrentHistoryVersion: () => void;
  resetQuery: () => void;
  deliveryData: PlanningsDeliveryEntity[];
}

export const useRollbackRequest = ({
  currentHistoryVersion,
  resetCurrentHistoryVersion,
  resetQuery,
  deliveryData,
}: RollbackRequestHookParams) => {
  const licenseContext = useContext(LicenseContext);
  const plansContext = useContext(PlansContext);
  const {
    deliveryEntities, generateRandomString, latestAlgorithmRequestVersion,
    startOn, endOn, handleErrorResponse,
  } = plansContext;

  const { enqueueSnackbar } = useSnackbar();
  const { rollback } = useMutationPlanning();
  const queryClient = useQueryClient();

  const rollbackRequest = useCallback(() => {
    if (!currentHistoryVersion) return;

    const deliveryIds: number[] = deliveryEntities.map(({ id }) => id);
    const current = plansContext.allocateHistories.find(({ version }) => version === currentHistoryVersion);

    if (![deliveryIds.length, current].every((maybe) => !!maybe)) return;

    enqueueSnackbar('操作を取り消します');

    plansContext.setCalculating(true);
    plansContext.setIsLoading(true);

    const orderOperations: PlanningsOperationEntityWithStatsEntity[] = current.deliveryEntities.flatMap(({ operations: ops }) => ops as unknown as PlanningsOperationEntityWithStatsEntity);
    orderOperations.forEach((it) => delete it.timeWindow);

    const arrivalOperations: BaseOperationEntity[] = current.deliveryEntities
      .flatMap(({ arrivalOperation }) => arrivalOperation)
      .filter((maybe) => maybe);
    const departureOperations: BaseOperationEntity[] = current.deliveryEntities
      .flatMap(({ departureOperation }) => departureOperation)
      .filter((maybe) => maybe);
    const distanceMmBurdenPerShippersEntities: distanceMmBurdenPerShippersEntity[] = current.deliveryEntities
      .flatMap(({ distanceMmBurdenPerShippers }) => distanceMmBurdenPerShippers)
      .filter((maybe) => maybe);
    const orderAssignments: OrderAssignment[] = Array.from(
      new Map(
        current.deliveryEntities.flatMap((it) => it.orderAssignments).filter((it) => it).map((item) => [item.id, item])
      ).values()
    );

    const allocateHistoryEntity: AllocateHistoryEntity = {
      version: generateRandomString(20),
      companyId: licenseContext.config?.selected_company_id,
      actAt: new Date(),
      action: '取消',
      message: `${datetimeDecorator.toHourMinuteSeconds(current.actAt)}までまで元に戻しました`,
      deliveryEntities: deliveryData,
    };

    rollback.mutate(
      {
        deliveryIds,
        orderOperations,
        arrivalOperations,
        departureOperations,
        distanceMmBurdenPerShippersEntities,
        orderAssignments,
        latestAlgorithmRequestVersion,
        startOn,
        endOn,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('操作を取り消しました');

          plansContext.addAllocateHistoryEntity(allocateHistoryEntity);

          queryClient.clear();
          const oIds = arrayUtil.uniq(orderOperations.map((it) => it.orderId).sort());
          const deallocatedOrderIds = plansContext.allocatedOrderIds.filter((it) => !oIds.includes(it));
          plansContext.removeAllocatedOrderIds(deallocatedOrderIds);
        },
        onError: (e: AxiosError<{ message: string; }>) => {
          handleErrorResponse(e, resetQuery);
        },
        onSettled: () => {
          plansContext.setIsLoading(false);
          plansContext.setCalculating(false);
          resetCurrentHistoryVersion();
          queryClient.clear();
        }
      }
    );
  }, [currentHistoryVersion, deliveryData, deliveryEntities, endOn, enqueueSnackbar, generateRandomString, handleErrorResponse, latestAlgorithmRequestVersion, licenseContext.config?.selected_company_id, plansContext, queryClient, resetCurrentHistoryVersion, resetQuery, rollback, startOn]);

  return { rollbackRequest };
};
